import React, { useState, useEffect } from "react";
import { observer } from "mobx-react";
import { makeApi, URLS } from "Utils/apiURL";
import { mapAiNotes, mapTranscriptLines } from "Utils/AiNotesUtils";
import CallsUtils from "Utils/CallsUtils";
import DateUtils from "Utils/DateUtils";
import ConstantObj, { SERVER_URL } from "Static/constant";
import NotesTab from "./NotesTab";
import AiNotesTab from "./AiNotesTab";
import TranscriptTab from "./TranscriptTab";
import AudioPlayer from "./AudioPlayer";
import ShareRecordingModal from "./ShareRecordingModal";
import { toasterState } from "Components/common/toaster";
import SearchIcon from "Assets/svg/search.svg";

const TABS = [
  { id: "notes", label: "Overview" },
  { id: "aiNotes", label: "AI Notes" },
  { id: "transcript", label: "Transcript" }
];

const getFormattedDate = (createTs = "") => {
  if (!createTs) return "--";
  const d = new Date(createTs);
  const hour = DateUtils.getHourValue(d.getHours());
  const minutes = DateUtils.getMinutesValue(d.getMinutes());
  const meridian = DateUtils.getMerdians(d.getHours());
  const date = `${ConstantObj?.months?.[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  const time = `${hour}:${minutes} ${meridian}`;
  return `${date} · ${time}`;
};

const OUTCOME_CLASSES = {
  connected: "outcomeTagConnected",
  "no answer": "outcomeTagNoAnswer",
  "left voicemail": "outcomeTagVoicemail",
  "not interested": "outcomeTagNotInterested",
  "wrong number": "outcomeTagWrongNumber"
};

const CallDetailPanel = observer(({ call = {} }) => {
  const {
    id = "",
    contactName = "",
    memberName = "",
    outcome = "",
    duration = 0,
    createTs = "",
    recordingUrl = "",
    notes = "",
    callSid = "",
    toNumber = ""
  } = call;

  const [activeTab, setActiveTab] = useState("notes");
  const [aiData, setAiData] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [transcriptLines, setTranscriptLines] = useState([]);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [generatingTranscript, setGeneratingTranscript] = useState(false);

  useEffect(() => {
    setActiveTab("notes");
  }, [id]);

  useEffect(() => {
    let cancelAiNotes = null;
    let cancelTranscript = null;

    setAiData(null);
    setTranscriptLines([]);
    setTranscriptLoading(true);

    if (callSid) {
      const fetchAiNotes = async () => {
        setAiLoading(true);
        try {
          const config = { url: `${URLS.generateAiCallNotes}?callSid=${callSid}`, method: "GET" };
          const res = await makeApi(config, cb => { cancelAiNotes = cb; });
          const { data = {} } = res || {};
          if (data?.aiNotes?.aiNotes) {
            setAiData(mapAiNotes(data.aiNotes.aiNotes));
          }
        } catch (e) {
          // silent fail (includes cancel)
        } finally {
          setAiLoading(false);
        }
      };

      const fetchTranscripts = async () => {
        setTranscriptLoading(true);
        try {
          const config = { url: `${URLS.getCallTranscript}?callSid=${callSid}`, method: "GET" };
          const res = await makeApi(config, cb => { cancelTranscript = cb; });
          const lines = mapTranscriptLines(res?.data?.transcripts || []);
          setTranscriptLines(lines);
        } catch (e) {
          // silent fail (includes cancel)
        } finally {
          setTranscriptLoading(false);
        }
      };

      fetchAiNotes();
      fetchTranscripts();
    } else {
      setTranscriptLoading(false);
    }

    return () => {
      cancelAiNotes && cancelAiNotes("cancel");
      cancelTranscript && cancelTranscript("cancel");
    };
  }, [callSid]);

  const handleGenerateTranscript = async () => {
    setGeneratingTranscript(true);
    try {
      const config = { url: `${URLS.transcribeFromRecording}?callSid=${callSid}`, method: "POST" };
      const res = await makeApi(config);
      const code = res?.data?.responseCode;
      if (code === 200) {
        const lines = mapTranscriptLines(res?.data?.transcripts || []);
        setTranscriptLines(lines);
      } else if (code === 204) {
        toasterState.setToastMsg("Couldn't generate transcript. The recording is too short or doesn't contain enough clear speech.", "failure");
      } else if (code === 405) {
        toasterState.setToastMsg("Something went wrong. Please contact support.", "failure");
        window.Intercom && window.Intercom("show");
      } else {
        toasterState.setToastMsg("Something went wrong", "failure");
      }
    } catch (e) {
      toasterState.setToastMsg("Something went wrong", "failure");
    } finally {
      setGeneratingTranscript(false);
    }
  };

  const handleShareRecording = async () => {
    setSharingLoading(true);
    try {
      const res = await makeApi({
        url: `${SERVER_URL}/cm/phone/recording/share/generate?callSid=${callSid}`,
        method: "POST"
      });
      const url = res?.data?.shareUrl;
      if (url) {
        setShareUrl(url);
      } else {
        toasterState.setToastMsg("Failed to generate share link. Please try again.", "failure");
      }
    } catch (e) {
      toasterState.setToastMsg("Failed to generate share link. Please try again.", "failure");
    } finally {
      setSharingLoading(false);
    }
  };

  const handleDownloadRecording = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${SERVER_URL}/cm/phone/recording/download?callSid=${callSid}`, {
        credentials: "include"
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const code = data?.responseCode || res.status;
        if (code === 404) {
          toasterState.setToastMsg("Recording not found", "failure");
        } else {
          toasterState.setToastMsg("Failed to download recording. Please try again.", "failure");
        }
        return;
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `recording-${callSid}.mp3`;
      a.click();
      URL.revokeObjectURL(objectUrl);
      toasterState.setToastMsg("Recording downloaded successfully", "success");
    } catch (e) {
      toasterState.setToastMsg("Failed to download recording. Please try again.", "failure");
    } finally {
      setDownloading(false);
    }
  };

  const hasAiNotes = !!callSid;
  const outcomeClass =
    OUTCOME_CLASSES[outcome?.toLowerCase()] || "outcomeTagDefault";
  const formattedDateTime = getFormattedDate(createTs);
  const formattedDuration = CallsUtils.secondsToTime(duration);

  if (!id) {
    return (
      <div className="callDetailPanel callDetailPanelEmpty">
        <div className="callDetailPanelEmptyIcon">
          <i className="material-icons-outlined">phone_in_talk</i>
        </div>
        <p className="callDetailPanelEmptyText">Select a call to view details</p>
      </div>
    );
  }

  return (
    <div className="callDetailPanel">
      <div className="callDetailPanelHeader">
        <div className="callDetailPanelHeaderTop">
          <div className="callDetailPanelNameBlock">
            <span className="callDetailPanelContactName">{contactName || toNumber || "--"}</span>
            <div className="callDetailPanelMetaRow">
              <span className="callDetailPanelMetaItem">
                <i className="material-icons-outlined">schedule</i>
                {formattedDuration}
              </span>
              <span className="callDetailPanelMetaItem">
                <i className="material-icons-outlined">person</i>
                {memberName || "--"}
              </span>
              <span className="callDetailPanelMetaItem">
                <i className="material-icons-outlined">calendar_today</i>
                {formattedDateTime}
              </span>
              {hasAiNotes && (
                <span className="callDetailPanelAiBadge">
                  <i className="material-icons">stars</i>
                  AI Notes
                </span>
              )}
            </div>
          </div>
          <span className={`outcomeTag ${outcomeClass}`}>{outcome || "--"}</span>
        </div>

        {recordingUrl && (
          <div className="callDetailPanelRecordingBlock">
            <AudioPlayer src={recordingUrl} totalDuration={duration} />
            <div className="callDetailPanelRecordingActions">
              <div className="callDetailPanelDownloadSlot">
                {downloading ? (
                  <span className="callDetailPanelDownloadingText">Downloading</span>
                ) : (
                  <button className="callDetailPanelActionBtn" onClick={handleDownloadRecording}>
                    <i className="material-icons">file_download</i>
                    Download Recording
                  </button>
                )}
              </div>
              <div className="callDetailPanelShareSlot">
                {sharingLoading ? (
                  <span className="callDetailPanelGeneratingText">Generating</span>
                ) : (
                  <button className="callDetailPanelActionBtn" onClick={handleShareRecording}>
                    <i className="material-icons">share</i>
                    Share Recording
                  </button>
                )}
              </div>
              {transcriptLines.length > 0 ? (
                <span className="callDetailPanelTranscriptReady">
                  <i className="material-icons">check</i>
                  Transcript ready
                </span>
              ) : generatingTranscript || transcriptLoading ? (
                <div className="callDetailPanelLinearBg" />
              ) : (
                <button
                  className="callDetailPanelActionBtn"
                  onClick={handleGenerateTranscript}
                >
                  <img src={SearchIcon} alt="" className="callDetailPanelBtnIcon" />
                  Generate transcript
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="callDetailPanelTabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`callDetailPanelTab${activeTab === tab.id ? " callDetailPanelTabActive" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.id === "aiNotes" && hasAiNotes && (
              <span className="callDetailPanelTabDot" />
            )}
            {tab.id === "transcript" && recordingUrl && (
              <span className="callDetailPanelTabDot" />
            )}
          </button>
        ))}
      </div>

      <div className="callDetailPanelBody">
        {activeTab === "notes" && <NotesTab call={call} aiSummary={aiData?.summary || ""} aiLoading={aiLoading} />}
        {activeTab === "aiNotes" && <AiNotesTab call={call} aiData={aiData} aiLoading={aiLoading} />}
        {activeTab === "transcript" && (
          <TranscriptTab
            call={call}
            transcriptLines={transcriptLines}
            loading={transcriptLoading}
            generating={generatingTranscript}
            onGenerate={handleGenerateTranscript}
          />
        )}
      </div>
      {shareUrl && (
        <ShareRecordingModal
          shareUrl={shareUrl}
          onClose={() => setShareUrl(null)}
          contactName={contactName || toNumber}
          memberName={memberName}
          duration={formattedDuration}
          callDate={getFormattedDate(createTs)}
        />
      )}
    </div>
  );
});

export default CallDetailPanel;
